import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import Index from "../../../../component/Index";
import {
  getSalesOrderPrintDataWebviewApiHandler,
  sizeMasterListWithoutAuthApiHandler,
} from "../../../../services/AdminIndex";
import moment from "moment";

// Mobile app webview of the Sales Order preview. The markup below is copied
// as-is from salesorder/Preview.js; only the data source differs: like the
// other webviews (/webview-stock-report/:companyId/...) the company comes
// from the URL — /webview-sales-order/:id/:companyId — not from a login.
const PAGE_WIDTH = 1000;

export default function SalesOrderPreviewWebview() {
  const { id, companyId } = useParams();
  const [productData, setProductData] = useState([]);
  const [companyAddress, setCompanyAddress] = useState();
  const [partyAddress, setPartyAddress] = useState();
  const [printData, setPrintData] = useState({});
  const [getSizeMaster, setGetSizeMaster] = useState([]);
  const [status, setStatus] = useState("loading");
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef();
  const printRef = useRef();
  const dataPerPage = 29;

  // Same print as salesorder/Preview.js: prints the hidden, paginated
  // PrintPDFComponent (copied below) rather than the scaled on-screen view.
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "Sales Order Invoice",
  });

  const parseData = (data) => {
    try {
      if (data) {
        let result = JSON.parse(data);
        return result;
      } else {
        return [];
      }
    } catch (e) {
      return [];
    }
  };

  useEffect(() => {
    Promise.all([
      sizeMasterListWithoutAuthApiHandler(companyId),
      getSalesOrderPrintDataWebviewApiHandler(id, companyId),
    ]).then(([sizeRes, data]) => {
      if (sizeRes?.status === 200) {
        setGetSizeMaster(sizeRes?.data || []);
      }
      if (data?.status === 201 || data?.status === 200) {
        setPrintData(data?.data);
        const companyAddress = parseData(data?.data?.companyAddress);
        setCompanyAddress(companyAddress?.[0]);
        const productSizeData = parseData(data?.data?.productData);

        const partyAddress = parseData(data?.data?.partyAddress);
        setPartyAddress(partyAddress?.[0]);

        let productArr = [];
        productSizeData?.map((row, index) => {
          let sizeDataArray = [];
          row?.sizeQuantity?.map((size) => {
            let sizeObject = {
              sizeId: size?.sizeId,
              quantity: size?.qty,
            };
            sizeDataArray[size?.sizeId] = sizeObject;
          });
          const rowObject = {
            ProductName: row?.ProductName,
            ProdDescription: row?.ProdDescription,
            setName: row?.setName,
            color_name: row?.color_name,
            qty: row?.qty,
            remarks: row?.remarks,
            sizeQuantity: sizeDataArray,
          };

          productArr.push(rowObject);
        });

        setProductData(productArr);
        setStatus(data?.data ? "ready" : "error");
      } else {
        setStatus("error");
      }
    });
  }, [id, companyId]);

  // The preview is laid out at desktop width (many size columns), so it is
  // scaled down to the phone's width instead of reflowed — keeps it looking
  // exactly like the web preview; the user can pinch-zoom to read.
  useEffect(() => {
    const updateScale = () =>
      setScale(
        Math.min(1, document.documentElement.clientWidth / PAGE_WIDTH)
      );
    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;
    const observer = new ResizeObserver(() =>
      setContentHeight(contentRef.current?.offsetHeight || 0)
    );
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [status]);

  if (status !== "ready") {
    return (
      <div
        style={{
          padding: 30,
          textAlign: "center",
          fontSize: 14,
          color: "#666666",
        }}
      >
        {status === "loading"
          ? "Loading..."
          : "Unable to load sales order. Please try again."}
      </div>
    );
  }

  return (
    <>
    <div
      style={{
        width: "100%",
        overflow: "hidden",
        height: contentHeight * scale || "auto",
      }}
    >
      <div
        ref={contentRef}
        style={{
          // flow-root keeps the preview container's 15px margin inside this
          // box, so the measured height includes it and the bottom of the
          // table (Remark/Total row) isn't clipped.
          display: "flow-root",
          backgroundColor: "#ffffff",
          width: PAGE_WIDTH,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
      <div className="sales-order-print-preview-container">
        <div className="border sales-order-print">
          <table
            border={0}
            cellSpacing={0}
            cellPadding={0}
            style={{ border: 0, width: "100%" }}
            className="preview-sales-order"
          >
            <>
              <thead>
                <tr>
                  <td>
                    <table
                      border={0}
                      cellSpacing={0}
                      cellPadding={0}
                      style={{
                        borderTop: "1px solid #9f9e9e",
                        borderLeft: "1px solid #9f9e9e",
                        borderRight: "1px solid #9f9e9e",
                        width: "100%",
                      }}
                    >
                      <tr>
                        <td
                          style={{
                            width: "20%",
                            paddingLeft: "20px",
                          }}
                        >
                          {printData?.companyLogo ? (
                            <img
                              src={
                                process.env.REACT_APP_IMAGE_URL +
                                printData?.companyLogo
                              }
                              // height="60px"
                              // width="100px"
                              style={{
                                objectFit: "contain",
                                height: "100%",
                                width: "100%",
                                maxWidth: "100px",
                                maxHeight: "60px",
                              }}
                            />
                          ) : (
                            <></>
                          )}
                        </td>
                        <td>
                          <tr>
                            <td
                              style={{
                                fontSize: 15,
                                lineHeight: 1.2,
                                color: "#666666",
                                verticalAlign: "top",
                                borderBottom: 0,
                                textAlign: "center",
                                width: "40%",
                              }}
                            >
                              <h2
                                style={{
                                  color: "#222222",
                                  margin: "0px 0px 5px 0px",
                                  fontSize: "25px",
                                }}
                              >
                                {companyAddress?.Company_name}
                              </h2>
                            </td>
                          </tr>
                          <tr>
                            <td
                              style={{
                                borderBottom: 0,
                                fontSize: 11,
                                lineHeight: 1.2,
                                padding: "1px 10px 5px",
                                color: "#666666",
                                verticalAlign: "middle",
                                paddingTop: 0,
                                paddingBottom: 0,
                                textAlign: "center",
                              }}
                              className="address"
                            >
                              <p
                                style={{
                                  textAlign: "center",
                                  lineHeight: 1.2,
                                  margin: 0,
                                  fontSize: 11,
                                  color: "#666666",
                                }}
                              >
                                {`${companyAddress?.o_add1 || ""}, ${companyAddress?.o_add2 || ""
                                  }, `}
                              </p>
                              <p
                                style={{
                                  textAlign: "center",
                                  lineHeight: 1.2,
                                  margin: 0,
                                  fontSize: 11,
                                  color: "#666666",
                                }}
                              >
                                {`${companyAddress?.o_add3 || ""}, ${companyAddress?.cityName || ""
                                  } - ${companyAddress?.o_pin || ""}, ${companyAddress?.Statename || ""
                                  } - ${companyAddress?.sCode || ""}`}
                              </p>
                              <p
                                style={{
                                  textAlign: "center",
                                  lineHeight: 1.2,
                                  margin: 0,
                                  fontSize: 11,
                                  color: "#666666",
                                }}
                              >
                                Contact No : {printData.companyMobileNo}, Email
                                Id : {printData.companyEmail}
                              </p>
                              <p
                                style={{
                                  textAlign: "center",
                                  lineHeight: 2,
                                  margin: 0,
                                  fontWeight: "700",
                                  fontSize: 11,
                                  color: "#666666",
                                  letterSpacing: "1.2px",
                                }}
                              >
                                GSTIN NO : {printData.companyGSTNo}, PAN NO :
                                {printData.companyPanNo}
                              </p>
                              <p />
                            </td>
                          </tr>
                        </td>
                        <td style={{ width: "20%" }}></td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td
                    colSpan={5}
                    className="head-text"
                    style={{
                      border: "1px solid #9f9e9e",
                      fontSize: 11,
                      padding: "2px 10px",
                      color: "#666666",
                      textAlign: "center",
                      background: "#efefef",
                    }}
                  >
                    <b
                      style={{
                        fontSize: 11,
                        textAlign: "center",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        color: "#000000",
                        margin: 0,
                        padding: "0px 0",
                        width: "100%",
                        lineHeight: 1.2,
                      }}
                    >
                      SALES ORDER
                    </b>
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      fontSize: 13,
                      color: "#666666",
                      verticalAlign: "top",
                    }}
                  >
                    <table
                      cellSpacing={0}
                      cellPadding={0}
                      className="padding-none border-none"
                      style={{
                        width: "100%",
                        padding: 0,
                        border: 0,
                        borderRight: "1px solid #9f9e9e",
                        borderLeft: "1px solid #9f9e9e",
                        // borderBottom: "1px solid #9f9e9e",
                      }}
                    >
                      <tbody>
                        <tr>
                          <td
                            className="padding-none border-none"
                            width="50%"
                            style={{
                              fontSize: 10,
                              padding: 0,
                              color: "#666666",
                              verticalAlign: "top",
                              border: 0,
                            }}
                          >
                            <table
                              border={0}
                              cellSpacing={0}
                              cellPadding={0}
                              style={{
                                width: "100%",
                              }}
                            >
                              <tbody>
                                <tr>
                                  <td
                                    className="label"
                                    style={{
                                      borderRight: "1px solid #9f9e9e",
                                      fontSize: 10,
                                      padding: "1px 6px",
                                      color: "#666666",
                                      verticalAlign: "top",
                                    }}
                                  >
                                    <p style={{ margin: "0 0 0px" }}>
                                      <b style={{ color: "#222222" }}>
                                        {`To : ${printData?.party || ""}`}
                                      </b>
                                    </p>
                                    <p
                                      style={{
                                        margin: "0 0 0px",
                                        minHeight: "18px",
                                      }}
                                    >
                                      {partyAddress?.address1}
                                    </p>
                                    <p
                                      style={{
                                        margin: "0 0 0px",
                                        minHeight: "18px",
                                      }}
                                    >
                                      {partyAddress?.address2}
                                    </p>
                                    <p
                                      style={{
                                        margin: "0 0 0px",
                                        minHeight: "18px",
                                      }}
                                    >
                                      {partyAddress?.address3}
                                    </p>
                                    <p
                                      style={{
                                        margin: "0 0 0px",
                                        minHeight: "18px",
                                      }}
                                    >
                                      {`${partyAddress?.cityName || ""} - ${partyAddress?.pin || ""
                                        }, ${partyAddress?.Statename || ""} - ${partyAddress?.sCode || ""
                                        }`}
                                    </p>
                                    <p
                                      style={{
                                        margin: "0 0 0px",
                                        minHeight: "18px",
                                      }}
                                    >
                                      GSTIN NO : {printData?.partyGST || ""}
                                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;PAN
                                      NO : {printData?.partyPanNo || ""}
                                    </p>
                                    <p
                                      style={{
                                        margin: "0 0 0px",
                                        minHeight: "18px",
                                      }}
                                    >
                                      Contact No :{" "}
                                      {`${printData?.partyMobile || ""}/${printData?.partyOfficeNumber || ""
                                        }`}
                                    </p>
                                    {printData?.partyMsme &&
                                      printData?.partyMsme != 0 && (
                                        <p
                                          style={{
                                            margin: "0 0 0px",
                                            minHeight: "18px",
                                          }}
                                        >
                                          MSME No : {printData?.partyMsme || ""}
                                        </p>
                                      )}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td
                            className="padding-none border-none"
                            width="50%"
                            style={{
                              fontSize: 10,
                              padding: 0,
                              color: "#666666",
                              verticalAlign: "top",
                              border: 0,
                            }}
                          >
                            <table
                              border={0}
                              cellSpacing={0}
                              cellPadding={0}
                              style={{
                                width: "100%",
                              }}
                            >
                              <tbody>
                                <tr>
                                  <td
                                    className="label"
                                    style={{
                                      fontSize: 10,
                                      lineHeight: 1.5,
                                      padding: "3.5px 6px",
                                      color: "#666666",
                                      verticalAlign: "top",
                                      width: "50%",
                                    }}
                                  >
                                    <b style={{ color: "#222222" }}>
                                      Order No:
                                    </b>
                                    <span style={{ paddingLeft: "18px" }}>
                                      {printData?.orderNo}
                                    </span>
                                  </td>
                                  {/* <td
                                    style={{
                                      fontSize: 10,
                                      lineHeight: 1.5,
                                      padding: "3.5px 6px",
                                      color: "#666666",
                                      verticalAlign: "top",
                                    }}
                                  >
                                    {printData?.orderNo}
                                  </td> */}
                                </tr>
                                <tr>
                                  <td
                                    className="label"
                                    style={{
                                      fontSize: 10,
                                      lineHeight: 1.5,
                                      padding: "3.5px 6px",
                                      color: "#666666",
                                      verticalAlign: "top",
                                      width: "50%",
                                    }}
                                  >
                                    <b style={{ color: "#222222" }}>
                                      Order Date:
                                    </b>
                                    <span style={{ paddingLeft: "10px" }}>
                                      {moment(printData?.orderDate).format(
                                        "DD/MM/YYYY"
                                      )}
                                    </span>
                                  </td>
                                  {/* <td
                                    style={{
                                      fontSize: 10,
                                      lineHeight: 1.5,
                                      padding: "3.5px 6px",
                                      color: "#666666",
                                      verticalAlign: "top",
                                    }}
                                  >
                                    {moment(printData?.orderDate).format(
                                      "DD/MM/YYYY"
                                    )}
                                  </td> */}
                                </tr>
                                <tr>
                                  <td
                                    className="label"
                                    style={{
                                      fontSize: 10,
                                      lineHeight: 1.5,
                                      padding: "3.5px 6px",
                                      color: "#666666",
                                      verticalAlign: "top",
                                      width: "50%",
                                    }}
                                  >
                                    <b style={{ color: "#222222" }}>Broker:</b>
                                    <span style={{ paddingLeft: "30px" }}>
                                      {printData?.broker}
                                    </span>
                                  </td>
                                  {/* <td
                                    style={{
                                      fontSize: 10,
                                      lineHeight: 1.5,
                                      padding: "3.5px 6px",
                                      color: "#666666",
                                      verticalAlign: "top",
                                    }}
                                  >
                                    {printData.broker}
                                  </td> */}
                                </tr>
                                <tr>
                                  <td
                                    className="label"
                                    style={{
                                      fontSize: 10,
                                      lineHeight: 1.5,
                                      padding: "3.5px 6px",
                                      color: "#666666",
                                      verticalAlign: "top",
                                      width: "50%",
                                    }}
                                  >
                                    <b style={{ color: "#222222" }}>
                                      Transport:
                                    </b>
                                    <span style={{ paddingLeft: "16px" }}>
                                      {printData?.transport}
                                    </span>
                                  </td>
                                  {/* <td
                                    style={{
                                      fontSize: 10,
                                      lineHeight: 1.5,
                                      padding: "3.5px 6px",
                                      color: "#666666",
                                      verticalAlign: "top",
                                    }}
                                  >
                                    {printData?.transport}
                                  </td> */}
                                </tr>
                                <tr>
                                  {printData?.party != printData?.tempParty &&
                                    printData?.tempParty != "" && (
                                      <td
                                        className="label"
                                        style={{
                                          fontSize: 10,
                                          lineHeight: 1.5,
                                          padding: "3.5px 6px",
                                          color: "#666666",
                                          verticalAlign: "top",
                                          width: "50%",
                                        }}
                                      >
                                        <b style={{ color: "#222222" }}>
                                          Order Party:
                                        </b>
                                        <span style={{ paddingLeft: "8px" }}>
                                          {printData?.tempParty}
                                        </span>
                                      </td>
                                    )}
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
                {/* <tr>
                  <td
                    colSpan={5}
                    style={{
                      fontSize: 10,
                      padding: "2px 6px",
                      color: "#666666",
                      verticalAlign: "top",
                    }}
                  />
                </tr> */}
              </thead>

              <tbody>
                {/* <tr>
                <td
                  colSpan={5}
                  style={{
                    fontSize: 10,
                    padding: "2px 6px",
                    color: "#666666",
                    verticalAlign: "top",
                  }}
                />
              </tr> */}
                <tr>
                  <td>
                    <table
                      border={0}
                      cellSpacing={0}
                      cellPadding={0}
                      style={{ border: "1px solid #9f9e9e" }}
                    >
                      <tbody>
                        <tr>
                          <td
                            style={{
                              verticalAlign: "top",
                              color: "#222222",
                              fontSize: 11,
                            }}
                          >
                            <table border={0} cellSpacing={0} cellPadding={0}>
                              <tbody>
                                <tr>
                                  <td
                                    style={{
                                      borderBottom: "1px solid #9f9e9e",
                                      borderRight: "1px solid #9f9e9e",
                                      color: "#222222",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "3px 6px",
                                      height: 25,
                                      background: "#efefef",
                                      width: "3%",
                                    }}
                                  >
                                    <b style={{ color: "#222222" }}>Sr.</b>
                                  </td>
                                  <td
                                    style={{
                                      borderBottom: "1px solid #9f9e9e",
                                      borderRight: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "3px 6px",
                                      height: 25,
                                      background: "#efefef",
                                      width: "15%",
                                    }}
                                  >
                                    <b style={{ color: "#222222" }}>
                                      Design No.
                                    </b>
                                  </td>
                                  <td
                                    style={{
                                      borderBottom: "1px solid #9f9e9e",
                                      borderRight: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "3px 6px",
                                      height: 25,
                                      background: "#efefef",
                                      width: "8%",
                                    }}
                                  >
                                    <b style={{ color: "#222222" }}>Colour</b>
                                  </td>
                                  <td
                                    style={{
                                      borderBottom: "1px solid #9f9e9e",
                                      borderRight: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "3px 6px",
                                      height: 25,
                                      background: "#efefef",
                                      width: "6%",
                                    }}
                                  >
                                    <b style={{ color: "#222222" }}>Set</b>
                                  </td>
                                  <td
                                    style={{
                                      borderBottom: "1px solid #9f9e9e",
                                      borderRight: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "3px 6px",
                                      height: 25,
                                      background: "#efefef",
                                      width: "12%",
                                    }}
                                  >
                                    <b style={{ color: "#222222" }}>Remarks</b>
                                  </td>
                                  <td
                                    colSpan={getSizeMaster?.length}
                                    style={{
                                      borderBottom: "1px solid #9f9e9e",
                                      borderRight: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "3px 6px",
                                      height: 25,
                                      background: "#efefef",
                                      textAlign: "center",
                                    }}
                                  >
                                    <b style={{ color: "#222222" }}>
                                      Size Details
                                    </b>
                                  </td>
                                  <td
                                    style={{
                                      borderBottom: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "3px 6px",
                                      height: 25,
                                      background: "#efefef",
                                    }}
                                    colSpan={2}
                                  >
                                    <b style={{ color: "#222222" }}>Qty</b>
                                  </td>
                                </tr>
                                <tr>
                                  <td
                                    style={{
                                      borderBottom: "1px solid #9f9e9e",
                                      borderRight: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "3px 6px",
                                      height: 25,
                                    }}
                                  ></td>
                                  <td
                                    style={{
                                      borderBottom: "1px solid #9f9e9e",
                                      borderRight: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "3px 6px",
                                      height: 25,
                                    }}
                                  ></td>
                                  <td
                                    style={{
                                      borderBottom: "1px solid #9f9e9e",
                                      borderRight: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "3px 6px",
                                      height: 25,
                                    }}
                                  ></td>
                                  <td
                                    style={{
                                      borderBottom: "1px solid #9f9e9e",
                                      borderRight: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "3px 6px",
                                      height: 25,
                                    }}
                                  ></td>
                                  <td
                                    style={{
                                      borderBottom: "1px solid #9f9e9e",
                                      borderRight: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "3px 6px",
                                      height: 25,
                                    }}
                                  ></td>
                                  {/* <td
                                    style={{
                                      borderBottom: "1px solid #9f9e9e",
                                      borderRight: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "3px 6px",
                                      height: 25,
                                    }}
                                  ></td> */}
                                  {getSizeMaster
                                    ?.sort((a, b) => a.type - b.type)
                                    ?.map((row, index) => (
                                      <td
                                        style={{
                                          borderBottom: "1px solid #9f9e9e",
                                          borderRight: "1px solid #c0c0c0",
                                          fontSize: 11,
                                          padding: "3px 6px",
                                          height: 25,
                                          color: "#222222",
                                          verticalAlign: "top",
                                          width: "37px",
                                          maxWidth: "37px",
                                        }}
                                      >
                                        <b style={{ color: "#222222" }}>
                                          {row?.size_name}
                                        </b>
                                      </td>
                                    ))}
                                  <td
                                    style={{
                                      borderBottom: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "3px 6px",
                                      height: 25,
                                    }}
                                    colSpan={2}
                                  ></td>
                                </tr>
                                {productData.map((row, index) => (
                                  <tr>
                                    <td
                                      style={{
                                        borderBottom: "1px solid #9f9e9e",
                                        borderRight: "1px solid #9f9e9e",
                                        color: "#222222",
                                        verticalAlign: "top",
                                        fontSize: 9,
                                        padding: "2px 6px",
                                        height: 20,
                                      }}
                                      width={70}
                                    >
                                      {index + 1}
                                    </td>
                                    <td
                                      style={{
                                        borderBottom: "1px solid #9f9e9e",
                                        borderRight: "1px solid #9f9e9e",
                                        fontSize: 9,
                                        padding: "2px 6px",
                                        height: 20,
                                        color: "#222222",
                                        verticalAlign: "top",
                                        width: "84px",
                                        maxWidth: "84px",
                                      }}
                                    >
                                      {row?.ProductName}
                                    </td>
                                    <td
                                      style={{
                                        borderBottom: "1px solid #9f9e9e",
                                        borderRight: "1px solid #9f9e9e",
                                        fontSize: 9,
                                        padding: "2px 6px",
                                        height: 20,
                                        color: "#222222",
                                        verticalAlign: "top",
                                        width: "57px",
                                        maxWidth: "57px",
                                      }}
                                    >
                                      {row?.color_name}
                                    </td>
                                    <td
                                      style={{
                                        borderBottom: "1px solid #9f9e9e",
                                        borderRight: "1px solid #9f9e9e",
                                        fontSize: 9,
                                        padding: "2px 6px",
                                        height: 20,
                                        color: "#222222",
                                        verticalAlign: "top",
                                        width: "84px",
                                        maxWidth: "84px",
                                      }}
                                    >
                                      {row?.setName}
                                    </td>
                                    <td
                                      style={{
                                        borderBottom: "1px solid #9f9e9e",
                                        borderRight: "1px solid #9f9e9e",
                                        fontSize: 9,
                                        padding: "2px 6px",
                                        height: 20,
                                        color: "#222222",
                                        verticalAlign: "top",
                                        width: "87px",
                                        width: "85px",
                                        maxWidth: "150px",
                                      }}
                                    >
                                      {row?.remarks}
                                    </td>
                                    {/* <td
                                      style={{
                                        borderBottom: "1px solid #9f9e9e",
                                        borderRight: "1px solid #9f9e9e",
                                        fontSize: 9,
                                        padding: "2px 6px",
                                        height: 20,
                                        color: "#222222",
                                        verticalAlign: "top",
                                        width: "70px",
                                        maxWidth: "70px",
                                      }}
                                    >
                                      {row?.ProdDescription}
                                    </td> */}
                                    {getSizeMaster
                                      ?.sort((a, b) => a.type - b.type)
                                      ?.map((size, index) => (
                                        <td
                                          style={{
                                            borderBottom: "1px solid #9f9e9e",
                                            borderRight: "1px solid #c0c0c0",
                                            fontSize: 9,
                                            padding: "2px 6px",
                                            height: 20,
                                            color: "#222222",
                                            verticalAlign: "top",
                                            width: "37px",
                                            maxWidth: "37px",
                                          }}
                                        >
                                          <b style={{ color: "#222222" }}>
                                            {row?.sizeQuantity?.[
                                              size?.size_code
                                            ]?.quantity || ""}
                                          </b>
                                        </td>
                                      ))}
                                    <td
                                      style={{
                                        borderBottom: "1px solid #9f9e9e",
                                        fontSize: 9,
                                        padding: "2px 6px",
                                        height: 20,
                                        color: "#222222",
                                        verticalAlign: "top",
                                      }}
                                      colSpan={2}
                                    >
                                      <b style={{ color: "#222222" }}>
                                        {row?.qty}
                                      </b>
                                    </td>
                                  </tr>
                                ))}
                                {/* Blank filler rows. Preview.js prints the
                                    row index in white text, which shows up on
                                    this page's grey background — here the cells
                                    are empty and carry the column lines instead. */}
                                {Array.apply(null, {
                                  length: Math.max(0, 35 - productData?.length),
                                }).map((row, index) => (
                                  <tr key={`blank-${index}`}>
                                    {Array.from({ length: 5 }).map((c, ci) => (
                                      <td
                                        key={ci}
                                        style={{
                                          borderRight: "1px solid #9f9e9e",
                                          padding: "2px 6px",
                                          height: 20,
                                        }}
                                      ></td>
                                    ))}
                                    {getSizeMaster?.map((size, si) => (
                                      <td
                                        key={`s-${si}`}
                                        style={{
                                          borderRight: "1px solid #c0c0c0",
                                          padding: "2px 6px",
                                          height: 20,
                                        }}
                                      ></td>
                                    ))}
                                    <td
                                      colSpan={2}
                                      style={{ padding: "2px 6px", height: 20 }}
                                    ></td>
                                  </tr>
                                ))}

                                <tr>
                                  <td
                                    colSpan={getSizeMaster?.length + 4}
                                    style={{
                                      borderTop: "1px solid #9f9e9e",
                                      borderRight: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "2px 6px",
                                      height: 20,
                                    }}
                                  >
                                    <p
                                      style={{
                                        color: "#222222",
                                        fontWeight: "700",
                                      }}
                                    >
                                      Remark:{" "}
                                      <span
                                        style={{
                                          color: "rgb(102, 102, 102)",
                                          fontWeight: "400",
                                        }}
                                      >
                                        {printData?.remarks}
                                      </span>
                                    </p>
                                  </td>
                                  <td
                                    style={{
                                      borderRight: "1px solid #9f9e9e",
                                      borderTop: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "2px 6px",
                                      height: 20,
                                      textAlign: "right",
                                    }}
                                  >
                                    <b style={{ color: "#222222" }}>Total</b>
                                  </td>
                                  <td
                                    colSpan={getSizeMaster?.length + 6}
                                    style={{
                                      color: "#666666",
                                      borderTop: "1px solid #9f9e9e",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "2px 6px",
                                      height: 20,
                                    }}
                                  >
                                    <b style={{ color: "#222222" }}>
                                      {printData?.quantity}
                                    </b>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </>
          </table>
        </div>
      </div>
      </div>
    </div>
    {/* <div className="print-button btn-primary-main">
      <Index.Box className="btn-flex-end">
        <Index.Box className="btn-primary-main">
          <Index.Button
            variant="contained"
            onClick={() => handlePrint()}
            className="btn-primary"
          >
            Print
          </Index.Button>
        </Index.Box>
      </Index.Box>
    </div> */}

    <div style={{ display: "none" }}>
      <PrintPDFComponent
        printRef={printRef}
        printData={printData}
        productData={productData}
        dataPerPage={dataPerPage}
        companyAddress={companyAddress}
        partyAddress={partyAddress}
        getSizeMaster={getSizeMaster}
      />
    </div>
    </>
  );
}

const PrintPDFComponent = ({
  printRef,
  printData,
  productData,
  dataPerPage,
  companyAddress,
  partyAddress,
  getSizeMaster,
}) => {
  return (
    <div style={{ padding: 15 }} ref={printRef}>
      {Array.from({
        length: Math.ceil(productData?.length / dataPerPage),
      }).map((ele, i) => (
        <div style={{ paddingTop: i === 0 ? 0 : 15 }}>
          <div
            className="sales-order-print-preview-container"
            style={{ margin: 0 }}
          >
            <div className="border sales-order-print">
              <table
                border={0}
                cellSpacing={0}
                cellPadding={0}
                style={{ border: 0, width: "100%" }}
                className="preview-sales-order"
              >
                <>
                  <thead>
                    <tr>
                      <td>
                        <table
                          border={0}
                          cellSpacing={0}
                          cellPadding={0}
                          style={{
                            borderTop: "1px solid #9f9e9e",
                            borderLeft: "1px solid #9f9e9e",
                            borderRight: "1px solid #9f9e9e",
                            width: "100%",
                          }}
                        >
                          <tr>
                            <td
                              style={{
                                width: "20%",
                                paddingLeft: "20px",
                              }}
                            >
                              {printData?.companyLogo ? (
                                <img
                                  src={
                                    process.env.REACT_APP_IMAGE_URL +
                                    printData?.companyLogo
                                  }
                                  height="40px"
                                  width="100px"
                                />
                              ) : (
                                <></>
                              )}
                            </td>
                            <td>
                              <tr>
                                <td
                                  style={{
                                    fontSize: 15,
                                    lineHeight: 1.2,
                                    color: "#666666",
                                    verticalAlign: "top",
                                    borderBottom: 0,
                                    textAlign: "center",
                                    width: "40%",
                                  }}
                                >
                                  <h2
                                    style={{
                                      color: "#222222",
                                      margin: "0px 0px 5px 0px",
                                      fontSize: "25px",
                                    }}
                                  >
                                    {companyAddress?.Company_name}
                                  </h2>
                                </td>
                              </tr>
                              <tr>
                                <td
                                  style={{
                                    borderBottom: 0,
                                    fontSize: 11,
                                    lineHeight: 1.2,
                                    padding: "1px 10px 5px",
                                    color: "#666666",
                                    verticalAlign: "middle",
                                    paddingTop: 0,
                                    paddingBottom: 0,
                                    textAlign: "center",
                                  }}
                                  className="address"
                                >
                                  <p
                                    style={{
                                      textAlign: "center",
                                      lineHeight: 1.2,
                                      margin: 0,
                                      fontSize: 11,
                                      color: "#666666",
                                    }}
                                  >
                                    {`${companyAddress?.o_add1 || ""}, ${companyAddress?.o_add2 || ""
                                      },`}
                                  </p>
                                  <p
                                    style={{
                                      textAlign: "center",
                                      lineHeight: 1.2,
                                      margin: 0,
                                      fontSize: 11,
                                      color: "#666666",
                                    }}
                                  >
                                    {`${companyAddress?.o_add3 || ""}, ${companyAddress?.cityName || ""
                                      } - ${companyAddress?.o_pin || ""}, ${companyAddress?.Statename || ""
                                      } - ${companyAddress?.sCode || ""}`}
                                  </p>
                                  <p
                                    style={{
                                      textAlign: "center",
                                      lineHeight: 1.2,
                                      margin: 0,
                                      fontSize: 11,
                                      color: "#666666",
                                    }}
                                  >
                                    Contact No : {printData.companyMobileNo},
                                    Email Id : {printData.companyEmail}
                                  </p>
                                  <p
                                    style={{
                                      textAlign: "center",
                                      lineHeight: 2,
                                      margin: 0,
                                      fontWeight: "700",
                                      fontSize: 11,
                                      color: "#666666",
                                      letterSpacing: "1.2px",
                                    }}
                                  >
                                    GSTIN NO : {printData.companyGSTNo}, PAN NO
                                    :{printData.companyPanNo}
                                  </p>
                                  <p />
                                </td>
                              </tr>
                            </td>
                            <td style={{ width: "20%" }}></td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                    <tr>
                      <td
                        colSpan={5}
                        className="head-text"
                        style={{
                          border: "1px solid #9f9e9e",
                          fontSize: 11,
                          padding: "2px 10px",
                          color: "#666666",
                          textAlign: "center",
                          background: "#efefef",
                        }}
                      >
                        <b
                          style={{
                            fontSize: 11,
                            textAlign: "center",
                            textTransform: "uppercase",
                            fontWeight: 700,
                            color: "#000000",
                            margin: 0,
                            padding: "0px 0",
                            width: "100%",
                            lineHeight: 1.2,
                          }}
                        >
                          SALES ORDER
                        </b>
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          fontSize: 13,
                          color: "#666666",
                          verticalAlign: "top",
                        }}
                      >
                        <table
                          cellSpacing={0}
                          cellPadding={0}
                          className="padding-none border-none"
                          style={{
                            width: "100%",
                            padding: 0,
                            border: 0,
                            borderRight: "1px solid #9f9e9e",
                            borderLeft: "1px solid #9f9e9e",
                            // borderBottom: "1px solid #9f9e9e",
                          }}
                        >
                          <tbody>
                            <tr>
                              <td
                                className="padding-none border-none"
                                width="50%"
                                style={{
                                  fontSize: 10,
                                  padding: 0,
                                  color: "#666666",
                                  verticalAlign: "top",
                                  border: 0,
                                }}
                              >
                                <table
                                  border={0}
                                  cellSpacing={0}
                                  cellPadding={0}
                                  style={{
                                    width: "100%",
                                  }}
                                >
                                  <tbody>
                                    <tr>
                                      <td
                                        className="label"
                                        style={{
                                          borderRight: "1px solid #9f9e9e",
                                          fontSize: 10,
                                          padding: "1px 6px",
                                          color: "#666666",
                                          verticalAlign: "top",
                                        }}
                                      >
                                        <p style={{ margin: "0 0 0px" }}>
                                          <b style={{ color: "#222222" }}>
                                            {`To : ${printData?.party || ""}`}
                                          </b>
                                        </p>
                                        <p
                                          style={{
                                            margin: "0 0 0px",
                                            minHeight: "18px",
                                          }}
                                        >
                                          {partyAddress?.address1}
                                        </p>
                                        <p
                                          style={{
                                            margin: "0 0 0px",
                                            minHeight: "18px",
                                          }}
                                        >
                                          {partyAddress?.address2}
                                        </p>
                                        <p
                                          style={{
                                            margin: "0 0 0px",
                                            minHeight: "18px",
                                          }}
                                        >
                                          {partyAddress?.address3}
                                        </p>
                                        <p
                                          style={{
                                            margin: "0 0 0px",
                                            minHeight: "18px",
                                          }}
                                        >
                                          {`${partyAddress?.cityName || ""} - ${partyAddress?.pin || ""
                                            }, ${partyAddress?.Statename || ""
                                            } - ${partyAddress?.sCode || ""}`}
                                        </p>
                                        <p
                                          style={{
                                            margin: "0 0 0px",
                                            minHeight: "18px",
                                          }}
                                        >
                                          GSTIN NO : {printData?.partyGST || ""}
                                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;PAN
                                          NO : {printData?.partyPanNo || ""}
                                        </p>
                                        <p
                                          style={{
                                            margin: "0 0 0px",
                                            minHeight: "18px",
                                          }}
                                        >
                                          Contact No :{" "}
                                          {`${printData?.partyMobile || ""}/${printData?.partyOfficeNumber || ""
                                            }`}
                                        </p>
                                        {printData?.partyMsme &&
                                          printData?.partyMsme != 0 && (
                                            <p
                                              style={{
                                                margin: "0 0 0px",
                                                minHeight: "18px",
                                              }}
                                            >
                                              MSME No :{" "}
                                              {printData?.partyMsme || ""}
                                            </p>
                                          )}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </td>
                              <td
                                className="padding-none border-none"
                                width="50%"
                                style={{
                                  fontSize: 10,
                                  padding: 0,
                                  color: "#666666",
                                  verticalAlign: "top",
                                  border: 0,
                                }}
                              >
                                <table
                                  border={0}
                                  cellSpacing={0}
                                  cellPadding={0}
                                  style={{
                                    width: "100%",
                                  }}
                                >
                                  <tbody>
                                    <tr>
                                      <td
                                        className="label"
                                        style={{
                                          fontSize: 10,
                                          lineHeight: 1.5,
                                          padding: "3.5px 6px",
                                          color: "#666666",
                                          verticalAlign: "top",
                                          width: "50%",
                                        }}
                                      >
                                        <b style={{ color: "#222222" }}>
                                          Order No:
                                        </b>
                                        <span style={{ paddingLeft: "18px" }}>
                                          {printData?.orderNo}
                                        </span>
                                      </td>
                                      {/* <td
                                    style={{
                                      fontSize: 10,
                                      lineHeight: 1.5,
                                      padding: "3.5px 6px",
                                      color: "#666666",
                                      verticalAlign: "top",
                                    }}
                                  >
                                    {printData?.orderNo}
                                  </td> */}
                                    </tr>
                                    <tr>
                                      <td
                                        className="label"
                                        style={{
                                          fontSize: 10,
                                          lineHeight: 1.5,
                                          padding: "3.5px 6px",
                                          color: "#666666",
                                          verticalAlign: "top",
                                          width: "50%",
                                        }}
                                      >
                                        <b style={{ color: "#222222" }}>
                                          Order Date:
                                        </b>
                                        <span style={{ paddingLeft: "10px" }}>
                                          {moment(printData?.orderDate).format(
                                            "DD/MM/YYYY"
                                          )}
                                        </span>
                                      </td>
                                      {/* <td
                                    style={{
                                      fontSize: 10,
                                      lineHeight: 1.5,
                                      padding: "3.5px 6px",
                                      color: "#666666",
                                      verticalAlign: "top",
                                    }}
                                  >
                                    {moment(printData?.orderDate).format(
                                      "DD/MM/YYYY"
                                    )}
                                  </td> */}
                                    </tr>
                                    <tr>
                                      <td
                                        className="label"
                                        style={{
                                          fontSize: 10,
                                          lineHeight: 1.5,
                                          padding: "3.5px 6px",
                                          color: "#666666",
                                          verticalAlign: "top",
                                          width: "50%",
                                        }}
                                      >
                                        <b style={{ color: "#222222" }}>
                                          Broker:
                                        </b>
                                        <span style={{ paddingLeft: "30px" }}>
                                          {printData?.broker}
                                        </span>
                                      </td>
                                      {/* <td
                                    style={{
                                      fontSize: 10,
                                      lineHeight: 1.5,
                                      padding: "3.5px 6px",
                                      color: "#666666",
                                      verticalAlign: "top",
                                    }}
                                  >
                                    {printData.broker}
                                  </td> */}
                                    </tr>
                                    <tr>
                                      <td
                                        className="label"
                                        style={{
                                          fontSize: 10,
                                          lineHeight: 1.5,
                                          padding: "3.5px 6px",
                                          color: "#666666",
                                          verticalAlign: "top",
                                          width: "50%",
                                        }}
                                      >
                                        <b style={{ color: "#222222" }}>
                                          Transport:
                                        </b>
                                        <span style={{ paddingLeft: "16px" }}>
                                          {printData?.transport}
                                        </span>
                                      </td>
                                      {/* <td
                                    style={{
                                      fontSize: 10,
                                      lineHeight: 1.5,
                                      padding: "3.5px 6px",
                                      color: "#666666",
                                      verticalAlign: "top",
                                    }}
                                  >
                                    {printData?.transport}
                                  </td> */}
                                    </tr>
                                    <tr>
                                      {printData?.party !=
                                        printData?.tempParty &&
                                        printData?.tempParty != "" && (
                                          <td
                                            className="label"
                                            style={{
                                              fontSize: 10,
                                              lineHeight: 1.5,
                                              padding: "3.5px 6px",
                                              color: "#666666",
                                              verticalAlign: "top",
                                              width: "50%",
                                            }}
                                          >
                                            <b style={{ color: "#222222" }}>
                                              Order Party:
                                            </b>
                                            <span
                                              style={{ paddingLeft: "8px" }}
                                            >
                                              {printData?.tempParty}
                                            </span>
                                          </td>
                                        )}
                                    </tr>
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                    {/* <tr>
                      <td
                        colSpan={5}
                        style={{
                          fontSize: 10,
                          padding: "2px 6px",
                          color: "#666666",
                          verticalAlign: "top",
                        }}
                      />
                    </tr> */}
                  </thead>

                  <tbody>
                    <tr>
                      <td>
                        <table
                          border={0}
                          cellSpacing={0}
                          cellPadding={0}
                          style={{ border: "1px solid #9f9e9e" }}
                        >
                          <tbody>
                            <tr>
                              <td
                                style={{
                                  verticalAlign: "top",
                                  color: "#222222",
                                  fontSize: 11,
                                }}
                              >
                                <table
                                  border={0}
                                  cellSpacing={0}
                                  cellPadding={0}
                                >
                                  <tbody>
                                    <tr>
                                      <td
                                        style={{
                                          borderBottom: "1px solid #9f9e9e",
                                          borderRight: "1px solid #9f9e9e",
                                          color: "#222222",
                                          verticalAlign: "middle",
                                          fontSize: 11,
                                          padding: "3px 6px",
                                          height: 25,
                                          background: "#efefef",
                                          width: "3%",
                                        }}
                                      >
                                        <b style={{ color: "#222222" }}>Sr.</b>
                                      </td>
                                      <td
                                        style={{
                                          borderBottom: "1px solid #9f9e9e",
                                          borderRight: "1px solid #9f9e9e",
                                          color: "#666666",
                                          verticalAlign: "middle",
                                          fontSize: 11,
                                          padding: "3px 6px",
                                          height: 25,
                                          background: "#efefef",
                                          width: "15%",
                                        }}
                                      >
                                        <b style={{ color: "#222222" }}>
                                          Design No.
                                        </b>
                                      </td>
                                      <td
                                        style={{
                                          borderBottom: "1px solid #9f9e9e",
                                          borderRight: "1px solid #9f9e9e",
                                          color: "#666666",
                                          verticalAlign: "middle",
                                          fontSize: 11,
                                          padding: "3px 6px",
                                          height: 25,
                                          background: "#efefef",
                                          width: "8%",
                                        }}
                                      >
                                        <b style={{ color: "#222222" }}>
                                          Colour
                                        </b>
                                      </td>
                                      <td
                                        style={{
                                          borderBottom: "1px solid #9f9e9e",
                                          borderRight: "1px solid #9f9e9e",
                                          color: "#666666",
                                          verticalAlign: "middle",
                                          fontSize: 11,
                                          padding: "3px 6px",
                                          height: 25,
                                          background: "#efefef",
                                          width: "6%",
                                        }}
                                      >
                                        <b style={{ color: "#222222" }}>Set</b>
                                      </td>
                                      <td
                                        style={{
                                          borderBottom: "1px solid #9f9e9e",
                                          borderRight: "1px solid #9f9e9e",
                                          color: "#666666",
                                          verticalAlign: "middle",
                                          fontSize: 11,
                                          padding: "3px 6px",
                                          height: 25,
                                          background: "#efefef",
                                          width: "12%",
                                        }}
                                      >
                                        <b style={{ color: "#222222" }}>
                                          Remarks
                                        </b>
                                      </td>
                                      <td
                                        colSpan={getSizeMaster?.length}
                                        style={{
                                          borderBottom: "1px solid #9f9e9e",
                                          borderRight: "1px solid #9f9e9e",
                                          color: "#666666",
                                          verticalAlign: "middle",
                                          fontSize: 11,
                                          padding: "3px 6px",
                                          height: 25,
                                          background: "#efefef",
                                          textAlign: "center",
                                        }}
                                      >
                                        <b style={{ color: "#222222" }}>
                                          Size Details
                                        </b>
                                      </td>
                                      <td
                                        style={{
                                          borderBottom: "1px solid #9f9e9e",
                                          color: "#666666",
                                          verticalAlign: "middle",
                                          fontSize: 11,
                                          padding: "3px 6px",
                                          height: 25,
                                          background: "#efefef",
                                        }}
                                        colSpan={2}
                                      >
                                        <b style={{ color: "#222222" }}>Qty</b>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td
                                        style={{
                                          borderBottom: "1px solid #9f9e9e",
                                          borderRight: "1px solid #9f9e9e",
                                          color: "#666666",
                                          verticalAlign: "middle",
                                          fontSize: 11,
                                          padding: "3px 6px",
                                          height: 25,
                                        }}
                                      ></td>
                                      <td
                                        style={{
                                          borderBottom: "1px solid #9f9e9e",
                                          borderRight: "1px solid #9f9e9e",
                                          color: "#666666",
                                          verticalAlign: "middle",
                                          fontSize: 11,
                                          padding: "3px 6px",
                                          height: 25,
                                        }}
                                      ></td>
                                      <td
                                        style={{
                                          borderBottom: "1px solid #9f9e9e",
                                          borderRight: "1px solid #9f9e9e",
                                          color: "#666666",
                                          verticalAlign: "middle",
                                          fontSize: 11,
                                          padding: "3px 6px",
                                          height: 25,
                                        }}
                                      ></td>
                                      <td
                                        style={{
                                          borderBottom: "1px solid #9f9e9e",
                                          borderRight: "1px solid #9f9e9e",
                                          color: "#666666",
                                          verticalAlign: "middle",
                                          fontSize: 11,
                                          padding: "3px 6px",
                                          height: 25,
                                        }}
                                      ></td>
                                      <td
                                        style={{
                                          borderBottom: "1px solid #9f9e9e",
                                          borderRight: "1px solid #9f9e9e",
                                          color: "#666666",
                                          verticalAlign: "middle",
                                          fontSize: 11,
                                          padding: "3px 6px",
                                          height: 25,
                                        }}
                                      ></td>
                                      {/* <td
                                    style={{
                                      borderBottom: "1px solid #9f9e9e",
                                      borderRight: "1px solid #9f9e9e",
                                      color: "#666666",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                      padding: "3px 6px",
                                      height: 25,
                                    }}
                                  ></td> */}
                                      {getSizeMaster
                                        ?.sort((a, b) => a.type - b.type)
                                        ?.map((row, index) => (
                                          <td
                                            style={{
                                              borderBottom: "1px solid #9f9e9e",
                                              borderRight: "1px solid #c0c0c0",
                                              fontSize: 11,
                                              padding: "3px 6px",
                                              height: 25,
                                              color: "#222222",
                                              verticalAlign: "top",
                                              width: "37px",
                                              maxWidth: "37px",
                                            }}
                                          >
                                            <b style={{ color: "#222222" }}>
                                              {row?.size_name}
                                            </b>
                                          </td>
                                        ))}
                                      <td
                                        style={{
                                          borderBottom: "1px solid #9f9e9e",
                                          color: "#666666",
                                          verticalAlign: "middle",
                                          fontSize: 11,
                                          padding: "3px 6px",
                                          height: 25,
                                        }}
                                        colSpan={2}
                                      ></td>
                                    </tr>
                                    {productData
                                      ?.slice(
                                        i * dataPerPage,
                                        dataPerPage * (i + 1)
                                      )
                                      .map((row, index) => (
                                        <tr>
                                          <td
                                            style={{
                                              borderBottom: "1px solid #9f9e9e",
                                              borderRight: "1px solid #9f9e9e",
                                              color: "#222222",
                                              verticalAlign: "top",
                                              fontSize: 9,
                                              padding: "2px 6px",
                                              height: 20,
                                            }}
                                            width={70}
                                          >
                                            {dataPerPage * i + (index + 1)}
                                          </td>
                                          <td
                                            style={{
                                              borderBottom: "1px solid #9f9e9e",
                                              borderRight: "1px solid #9f9e9e",
                                              fontSize: 9,
                                              padding: "2px 6px",
                                              height: 20,
                                              color: "#222222",
                                              verticalAlign: "top",
                                              width: "84px",
                                              maxWidth: "84px",
                                            }}
                                          >
                                            {row?.ProductName}
                                          </td>
                                          <td
                                            style={{
                                              borderBottom: "1px solid #9f9e9e",
                                              borderRight: "1px solid #9f9e9e",
                                              fontSize: 9,
                                              padding: "2px 6px",
                                              height: 20,
                                              color: "#222222",
                                              verticalAlign: "top",
                                              width: "57px",
                                              maxWidth: "57px",
                                            }}
                                          >
                                            {row?.color_name}
                                          </td>
                                          <td
                                            style={{
                                              borderBottom: "1px solid #9f9e9e",
                                              borderRight: "1px solid #9f9e9e",
                                              fontSize: 9,
                                              padding: "2px 6px",
                                              height: 20,
                                              color: "#222222",
                                              verticalAlign: "top",
                                              width: "84px",
                                              maxWidth: "84px",
                                            }}
                                          >
                                            {row?.setName}
                                          </td>
                                          <td
                                            style={{
                                              borderBottom: "1px solid #9f9e9e",
                                              borderRight: "1px solid #9f9e9e",
                                              fontSize: 9,
                                              padding: "2px 6px",
                                              height: 20,
                                              color: "#222222",
                                              verticalAlign: "top",
                                              width: "87px",
                                              width: "85px",
                                              maxWidth: "150px",
                                            }}
                                          >
                                            {row?.remarks}
                                          </td>
                                          {/* <td
                                            style={{
                                              borderBottom: "1px solid #9f9e9e",
                                              borderRight: "1px solid #9f9e9e",
                                              fontSize: 9,
                                              padding: "2px 6px",
                                              height: 20,
                                              color: "#222222",
                                              verticalAlign: "top",
                                              width: "70px",
                                              maxWidth: "70px",
                                            }}
                                          >
                                            {row?.ProdDescription}
                                          </td> */}
                                          {getSizeMaster
                                            ?.sort((a, b) => a.type - b.type)
                                            ?.map((size, index) => (
                                              <td
                                                style={{
                                                  borderBottom:
                                                    "1px solid #9f9e9e",
                                                  borderRight:
                                                    "1px solid #c0c0c0",
                                                  fontSize: 9,
                                                  padding: "2px 6px",
                                                  height: 20,
                                                  color: "#222222",
                                                  verticalAlign: "top",
                                                  width: "37px",
                                                  maxWidth: "37px",
                                                }}
                                              >
                                                <b
                                                  style={{
                                                    color: "#222222",
                                                  }}
                                                >
                                                  {row?.sizeQuantity?.[
                                                    size?.size_code
                                                  ]?.quantity || ""}
                                                </b>
                                              </td>
                                            ))}
                                          <td
                                            style={{
                                              borderBottom: "1px solid #9f9e9e",
                                              fontSize: 9,
                                              padding: "2px 6px",
                                              height: 20,
                                              color: "#222222",
                                              verticalAlign: "top",
                                            }}
                                            colSpan={2}
                                          >
                                            <b style={{ color: "#222222" }}>
                                              {row?.qty}
                                            </b>
                                          </td>
                                        </tr>
                                      ))}

                                    {/* {Array.apply(null, {
                                          length: 35 - productData?.length,
                                          }).map((row, index) => (
                                            <tr>&nbsp;</tr>
                                            ))} */}

                                    {Math.ceil(
                                      productData?.length / dataPerPage
                                    ) -
                                      1 ===
                                      i && (
                                        <>
                                          {Array.from({
                                            length:
                                              dataPerPage -
                                              productData?.slice(
                                                dataPerPage *
                                                (Math.ceil(
                                                  productData?.length /
                                                  dataPerPage
                                                ) -
                                                  1)
                                              )?.length,
                                          }).map((row, index) => (
                                            <tr>
                                              <td
                                                style={{
                                                  fontSize: 9,
                                                  padding: "2px 6px",
                                                  height: 20,
                                                  color: "#ffffff",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                {index}
                                              </td>
                                            </tr>
                                          ))}
                                          <tr>
                                            <td
                                              colSpan={getSizeMaster?.length + 4}
                                              style={{
                                                borderTop: "1px solid #9f9e9e",
                                                borderRight: "1px solid #9f9e9e",
                                                color: "#666666",
                                                verticalAlign: "middle",
                                                fontSize: 11,
                                                padding: "2px 6px",
                                                height: 20,
                                              }}
                                            >
                                              <p
                                                style={{
                                                  color: "#222222",
                                                  fontWeight: "700",
                                                }}
                                              >
                                                Remark:{" "}
                                                <span
                                                  style={{
                                                    color: "rgb(102, 102, 102)",
                                                    fontWeight: "400",
                                                  }}
                                                >
                                                  {printData?.remarks}
                                                </span>
                                              </p>
                                            </td>
                                            <td
                                              style={{
                                                borderRight: "1px solid #9f9e9e",
                                                borderTop: "1px solid #9f9e9e",
                                                color: "#666666",
                                                verticalAlign: "middle",
                                                fontSize: 11,
                                                padding: "2px 6px",
                                                height: 20,
                                                textAlign: "right",
                                              }}
                                            >
                                              <b style={{ color: "#222222" }}>
                                                Total
                                              </b>
                                            </td>
                                            <td
                                              colSpan={getSizeMaster?.length + 6}
                                              style={{
                                                color: "#666666",
                                                borderTop: "1px solid #9f9e9e",
                                                verticalAlign: "middle",
                                                fontSize: 11,
                                                padding: "2px 6px",
                                                height: 20,
                                              }}
                                            >
                                              <b style={{ color: "#222222" }}>
                                                {printData?.quantity}
                                              </b>
                                            </td>
                                          </tr>
                                        </>
                                      )}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </tbody>
                </>
              </table>
            </div>
          </div>
          {(Math.ceil(productData?.length / dataPerPage) - 1 || 0) !== i && (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th
                    style={{
                      fontSize: "15px",
                      lineHeight: "20px",
                      fontWeight: 500,
                      color: "#666666",
                      margin: "0px",
                      textAlign: "center",
                      paddingTop: 10,
                    }}
                    colSpan={9}
                  >
                    {`Continue to page ${i + 2}`}
                  </th>
                </tr>
              </thead>
            </table>
          )}
        </div>
      ))}
    </div>
  );
};
